/**
 * "Ben gave you #412 while you were at lunch."
 *
 * WHAT THIS IS NOT: an inbox. There is no notification store behind it, no
 * read state of its own, and nothing to clear. The server derives the list
 * from the audit trail and each person's ticket read marks (see
 * services/assignments.ts), which means the only way to dismiss an item is
 * to open the ticket — which is the whole point of showing it.
 *
 * So there is deliberately no "mark all as seen". A button that emptied this
 * list without anybody looking at the work would be a button for making the
 * bell quiet, and a bell people silence is worse than no bell.
 *
 * TWO AUDIENCES, ONE COMPONENT. Somebody sitting on the page gets a toast
 * the moment the count rises; somebody who comes back after two days gets
 * the same list, because it was never a time window — it is "assigned to me
 * and not yet looked at", which is as true on Monday as it was on Friday.
 */
import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Bell, Bot } from 'lucide-react';
import { api, type AssignmentNotice } from '../lib/api.js';
import { PushSwitch } from './PushSwitch.js';
import { NotifySettings } from './NotifySettings.js';
import { Button, cn, Popover, PopoverContent, PopoverTrigger, Tooltip, useToast } from '../ui/index.js';

/**
 * Matches the queue's own refresh.
 *
 * Mail already takes its own time to arrive, so the poll is the one delay
 * here we control — every second of it is added to a lag somebody is already
 * waiting out.
 */
const POLL_MS = 30_000;

/** "just now", "4m", "2h" — the precision a glance can use. */
function ago(iso: string): string {
  const seconds = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (seconds < 45) return 'just now';
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${Math.max(1, minutes)}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
}

export interface Line {
  key: string;
  /** Who did it, as a sentence subject. */
  who: string;
  automated: boolean;
  notices: AssignmentNotice[];
}

/**
 * Group a run of the router's work into one line.
 *
 * A routing rule that assigns twelve tickets is ONE thing that happened, and
 * twelve identical rows would bury the one a colleague sent you by hand.
 * People are never grouped: "Ben gave you three" is worth reading as three
 * separate subjects, because you are going to answer them separately.
 *
 * Exported for the tests — the grouping is the only logic here.
 */
export function groupNotices(notices: AssignmentNotice[]): Line[] {
  const lines: Line[] = [];
  for (const n of notices) {
    const who = n.actorLabel ?? (n.automated ? 'Routing' : 'Somebody');
    const last = lines.at(-1);
    if (n.automated && last?.automated && last.who === who) {
      last.notices.push(n);
      continue;
    }
    lines.push({ key: n.ticketId, who, automated: n.automated, notices: [n] });
  }
  return lines;
}

export function NewAssignments({ tenant, enabled }: { tenant: string; enabled: boolean }) {
  const [open, setOpen] = useState(false);
  const { toast } = useToast();
  const seen = useRef<number | null>(null);
  const navigate = useNavigate();

  const q = useQuery({
    queryKey: ['new-assignments', tenant],
    queryFn: () =>
      api.get<{ assignments: AssignmentNotice[] }>(
        `/api/v1/tenants/${tenant}/assignments/new`,
      ),
    enabled: enabled && Boolean(tenant),
    refetchInterval: POLL_MS,
  });

  const notices = q.data?.assignments ?? [];
  const count = notices.length;

  /**
   * Only on a RISE, and never on first load.
   *
   * The first answer after signing in is a backlog, not an event — toasting
   * it would announce work you were told about on Friday. `seen` starts null
   * precisely so the first count can be adopted without saying anything.
   */
  useEffect(() => {
    if (q.data === undefined) return;
    const before = seen.current;
    seen.current = count;
    if (before === null || count <= before) return;
    const fresh = count - before;
    toast({
      tone: 'brand',
      title: fresh === 1 ? '1 ticket was assigned to you' : `${fresh} tickets were assigned to you`,
      action: { label: 'Show', onSelect: () => setOpen(true) },
      duration: 6000,
    });
  }, [q.data, count, toast]);

  if (!enabled) return null;

  const lines = groupNotices(notices);

  const goTo = (n: AssignmentNotice) => {
    setOpen(false);
    navigate(`/t/${tenant}/${n.number}`);
  };

  return (
    // Not a modal. Clicking anywhere else closes it, and nothing behind it
    // stops working while it is open — it is a glance, not a screen.
    <Popover open={open} onOpenChange={setOpen}>
      <Tooltip content={count === 0 ? 'Nothing new assigned to you' : 'Assigned to you'}>
        <span>
          <PopoverTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              iconOnly
              aria-label={
                count === 0
                  ? 'Nothing newly assigned to you'
                  : `${count} newly assigned to you`
              }
            >
              <span className="relative inline-flex">
                <Bell aria-hidden className="h-4 w-4" />
                {count > 0 && (
                  <span
                    aria-hidden
                    className="absolute -right-1.5 -top-1.5 flex h-3.5 min-w-3.5 items-center justify-center rounded-full bg-brand px-1 text-3xs font-semibold text-ink-on-brand"
                  >
                    {count > 9 ? '9+' : count}
                  </span>
                )}
              </span>
            </Button>
          </PopoverTrigger>
        </span>
      </Tooltip>

      <PopoverContent className="w-80 overflow-hidden p-0">
        <p className="border-b border-line-subtle px-3 py-2 text-2xs font-semibold uppercase tracking-wide text-ink-tertiary">
          Assigned to you
        </p>

        {lines.length === 0 ? (
          <p className="px-3 py-4 text-xs text-ink-tertiary">
            {/* Says what it means rather than "no notifications": this
                list is specifically about work handed to you, and it
                empties when you open the ticket. */}
            Nothing new. This clears itself when you open a ticket.
          </p>
        ) : (
          <ul className="max-h-80 divide-y divide-line-subtle overflow-y-auto">
            {lines.map((line) => {
              const first = line.notices[0]!;
              const many = line.notices.length > 1;
              return (
                <li key={line.key}>
                  <button
                    type="button"
                    onClick={() => goTo(first)}
                    className="block w-full px-3 py-2 text-left transition-colors hover:bg-surface-hover focus-visible:outline-none focus-visible:bg-surface-hover"
                  >
                    <span className="flex items-baseline gap-1.5">
                      {line.automated && (
                        <Bot aria-hidden className="h-3 w-3 shrink-0 translate-y-0.5 text-ink-tertiary" />
                      )}
                      <span className="text-xs text-ink">
                        <span className="font-medium">{line.who}</span>
                        {many ? (
                          <> assigned {line.notices.length} tickets to you</>
                        ) : (
                          <> assigned #{first.number} to you</>
                        )}
                      </span>
                    </span>
                    <span className="mt-0.5 flex items-baseline gap-2">
                      <span className={cn('truncate text-2xs', many ? 'text-ink-tertiary' : 'text-ink-secondary')}>
                        {many ? `starting with #${first.number}` : first.subject}
                      </span>
                      <span className="ml-auto shrink-0 text-2xs text-ink-tertiary">
                        {ago(first.at)}
                      </span>
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
        <NotifySettings tenant={tenant} />
        <PushSwitch tenant={tenant} />
      </PopoverContent>
    </Popover>
  );
}
